import { useCallback, useEffect, useRef, useState } from 'react';

export interface ElementLike {
  closest(selector: string): ElementLike | null;
  getBoundingClientRect(): DOMRect;
}

type ReturnType = [DOMRect, (node: ElementLike | null) => void];

const getDefaultDOMRect = (): DOMRect => new DOMRect();

function isNotVisible(rect: DOMRect) {
  return rect.height === 0 && rect.width === 0;
}

function nodeNoLongerInDocument(node: ElementLike) {
  return node.closest('body') === null;
}

export const useBoundingBox = (): ReturnType => {
  const rafCallback = useRef<number | null>(null);
  const isMounted = useRef(true);
  const prevNode = useRef<ElementLike | null>(null);
  const [boundingBox, setBoundingBox] = useState<DOMRect>(getDefaultDOMRect());

  const updateBoundingRect = useCallback(
    (node: ElementLike) => () => {
      // NOTE: this is required as the prev node (assumed new node emitted after null) might be no longer
      // in the document so it'll be always considered as "invisible" which would create infinite loop below
      if (nodeNoLongerInDocument(node)) {
        return;
      }

      const rect = node.getBoundingClientRect();

      if (isNotVisible(rect)) {
        // NOTE: raf is required as we need to wait for changes to be reflected in DOM and it's better than setTimeout
        // however it might be replaced with one
        rafCallback.current = requestAnimationFrame(updateBoundingRect(node));
      } else if (isMounted.current) {
        setBoundingBox(rect);
      }
    },
    [setBoundingBox],
  );

  const setRef = useCallback(
    (node: ElementLike | null) => {
      if (rafCallback.current) {
        cancelAnimationFrame(rafCallback.current);
      }

      const cachedPrevNode = prevNode.current;
      prevNode.current = node;

      // NODE: handle case when null is emitted after the new node callback
      if (node === null) {
        if (isMounted.current) {
          setBoundingBox(getDefaultDOMRect());
        }

        if (cachedPrevNode !== null) {
          // NOTE: raf is required for React to apply changes for null emitted previously in the same tick
          rafCallback.current = requestAnimationFrame(updateBoundingRect(cachedPrevNode));
        }

        return;
      }

      // NOTE: handle case when null is emitted before the new node callback
      const updateRect = updateBoundingRect(node);
      if (cachedPrevNode === null) {
        // NOTE: raf is required for React to apply changes for null emitted previously in the same tick
        requestAnimationFrame(updateRect);

        return;
      }

      // NOTE: this is when there is new node right after old node with no null between
      updateRect();
    },
    [setBoundingBox, updateBoundingRect],
  );

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  return [boundingBox, setRef];
};
